import React from 'react';
import Check from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import { VendorCell } from '../lists/invoiceList';
import { formatNumber } from '../../components/Common/Utilities';

// New UI for the master data lists (docs/ui_redesign/14-screen-concepts.md, "Master data lists", option A: tidy grid,
// edit in place) and User management. EnhancedDataGrid applies it on /Setup/ pages and /Resources only. Display only: fields, sorting, filtering and
// the lists' inline editing are unchanged.
export const isSetupPage = (pathname = (typeof window !== 'undefined' ? window.location.pathname : '')) => /^\/(setup\/|resources\/?$)/i.test(pathname || '');  // and User management (/Resources)

// Yes / no values shown as ✓ and —, like Insertion orders' own Active column. Y, 1 and true count as yes.
const FLAGS = new Set(['poRequiredFlag', 'tolerancePOFlag', 'costTypeStatus', 'expenseFlag']);
// The fields Classic's own save checks require (Currencies, Taxes, Payment terms, User statuses, Vendor site mapping):
// their columns show a required mark while they can be edited (R12).
export const REQUIRED = new Set(['currencyCode', 'currencyName', 'taxCode', 'otherTaxAmt', 'taxTypeCode', 'termsDesc', 'userStatusCode', 'userStatusDescription', 'vendorCode', 'siteId']);
const markRequired = c => (c && c.editable && REQUIRED.has(c.field) && typeof c.headerClassName !== 'function'
	? { ...c, headerClassName: [c.headerClassName, 'apw2-req-col'].filter(Boolean).join(' ') } : c);
export const isYes = v => v === true || v === 1 || v === '1' || String(v).toUpperCase() === 'Y';
const Flag = ({ on }) => (on
	? <Check fontSize="small" className="apw2-flag on" titleAccess="Yes" />
	: <RemoveIcon fontSize="small" className="apw2-flag" titleAccess="No" />);

// Consumed against the IO/PO amount, as a bar under the amount.
export function consumedShare(row) {
	const a = Number(row && row.poAmount) || 0;
	if (a <= 0) return null;
	return Math.max(0, Math.min(1, (Number(row.consumedAmount) || 0) / a));
}
function ConsumedCell({ row }) {
	const share = consumedShare(row);
	return (
		<span className="apw2-paid">
			<span>{formatNumber(row.consumedAmount) + ' ' + (row.currencyCode || '')}</span>
			{share != null ? <span className={'apw2-bar' + (share >= 1 ? ' t-warn' : ' t-info')} aria-hidden="true"><i style={{ width: Math.round(share * 100) + '%' }} /></span> : null}
		</span>
	);
}

// The wide lists get sizes that fit at 1440 px, shorter headers, and (Insertion orders) who-and-what first. A list is
// known by a field only it has. [flex, minWidth]; a flex of 0 is a fixed width.
const LAYOUTS = [
	{ key: 'poCode', order: ['poCode', 'vendor', 'clientCode', 'clientName', 'jobCode', 'campaignName', 'poAmount', 'consumedAmount', 'activeFlag', 'details', 'resources'],
		size: { poCode: [0.9, 124], vendor: [1.4, 170], clientCode: [0.5, 78], clientName: [1, 120], jobCode: [0.5, 78], campaignName: [1, 120], poAmount: [0.9, 124],
			consumedAmount: [1, 140], activeFlag: [0, 70], details: [0, 86], resources: [0, 100] },
		header: { clientCode: 'Client', clientName: 'Client name', jobCode: 'Job', campaignName: 'Campaign', consumedAmount: 'Consumed' } },
	{ key: 'taxCode',
		size: { taxCode: [0.7, 90], taxDesc: [1, 130], taxTypeCode: [0.6, 80], taxTypeDesc: [1, 140], taxType: [0.7, 100], otherTaxAmt: [0.5, 76],
			taxIncludedFlag: [0, 84], prcFlag: [0, 110], mappings: [0, 142] },
		header: { taxDesc: 'Description', taxTypeCode: 'Type', taxTypeDesc: 'Type description', taxType: 'Category', otherTaxAmt: 'Rate %', taxIncludedFlag: 'Included', prcFlag: 'Percentage' } },
	// User management: the person first, then email, title and Active; IDs, initials and login follow
	{ key: 'nameFirst', order: ['apadUserRolesResource', 'nameFirst', 'nameLast', 'email', 'title', 'activeFlag', 'resourceId', 'nameInit', 'loginId', 'actions'],
		size: { apadUserRolesResource: [1, 130], nameFirst: [0.8, 100], nameLast: [0.8, 100], email: [1.4, 180], title: [1, 110], activeFlag: [0, 70],
			resourceId: [0.5, 80], nameInit: [0, 86], loginId: [0.8, 100], actions: [0, 290] },
		header: { resourceId: 'ID', nameInit: 'Initials', apadUserRolesResource: 'User group' } },
	// Cost types: codes like "15 PRODBILL" stay on one line
	{ key: 'costType',
		size: { costType: [0.7, 130], costTypeName: [0.7, 120], costTypeDescription: [1.4, 180], costTypeStatus: [0, 80], effectiveDate: [0.6, 110], expirationDate: [0.6, 120] } },
	{ key: 'level2Key',
		size: { level2Key: [0.7, 100], level2Description: [1.4, 150], level2StatusName: [0.7, 110], openDate: [0.6, 88], customerCode: [0.5, 76], customerName: [1, 120],
			customerPONumber: [0.7, 100], poRequiredFlag: [0, 90], tolerancePOFlag: [0, 96], tolerancePOAmount: [0.7, 110], activities: [0, 96], resources: [0, 96] },
		header: { customerCode: 'Customer', customerName: 'Customer name', customerPONumber: 'Customer PO', tolerancePOAmount: 'Tolerance amount' } }
];

function layout(columns) {
	const l = LAYOUTS.find(x => columns.some(c => c && c.field === x.key));
	if (!l) return columns;
	const rank = c => { const i = (l.order || []).indexOf(c.field); return i < 0 ? 999 : i; };
	const sorted = l.order ? columns.map((c, i) => [c, i]).sort((a, b) => (rank(a[0]) - rank(b[0])) || (a[1] - b[1])).map(x => x[0]) : columns;
	return sorted.map(c => {
		const sz = c && l.size[c.field];
		if (!sz) return c;
		const [flex, minWidth] = sz;
		return { ...c, flex: flex || undefined, minWidth, width: flex ? undefined : minWidth, apw2Sized: true, ...((l.header || {})[c.field] ? { headerName: l.header[c.field] } : {}) };
	});
}

export function setupColumns(columns) {
	if (!Array.isArray(columns)) return columns;
	return layout(columns).map(markRequired).map(c => {
		if (!c || !c.field) return c;
		if (FLAGS.has(c.field)) return { ...c, align: 'center', headerAlign: 'center', renderCell: p => <Flag on={isYes(p.row[c.field])} /> };
		if (c.field === 'vendor') return { ...c, renderCell: p => <VendorCell value={p.value} /> };
		// A Mappings column's "Edit" link reads "Edit mappings", so it isn't confused with the row's own Edit. Same link.
		if (c.field === 'mappings' && c.renderCell) {
			const own = c.renderCell;
			return { ...c, minWidth: Math.max(c.minWidth || 0, 124), renderCell: p => {
				const el = own(p);
				return React.isValidElement(el) && el.props.children === 'Edit' ? React.cloneElement(el, {}, 'Edit mappings') : el;
			} };
		}
		if (c.field === 'consumedAmount') return { ...c, renderCell: p => <ConsumedCell row={p.row} /> };
		// Labelled Edit and Delete (Save and Cancel while editing) need room for their words
		if (c.type === 'actions' || /^actions?$/i.test(c.field)) return { ...c, minWidth: Math.max(c.minWidth || 0, 180), ...(c.width ? { width: Math.max(c.width, 180) } : {}) };
		return c;
	});
}
